/* Filter-first explorer (“Filter & Download” tab)
   — stable inputs + focus-aware suggestions + single TEMP table per selection
*/
import React, {
  useState, useEffect, useRef, useMemo, useCallback, memo,
} from "react";
import type { AsyncDuckDBConnection } from "@duckdb/duckdb-wasm";
import { arrowTableToObjects, useDebounce, exportToCsv } from "@/lib";
import DataTable from "@/core/data-tables/DataTable";
import { useQueryRunner } from "@/core/query/useQueryRunner";

import {
  Card, CardHeader, CardTitle, CardDescription, CardContent,
} from "@ui/card";
import { Button } from "@ui/button";
import { Input } from "@ui/input";
import { ScrollArea } from "@ui/scroll-area";
import {
  Filter as FilterIcon, Plus, X as CloseIcon, Check,
  Download as DownloadIcon,
} from "lucide-react";

/* ───────────────────────── constants ───────────────────────── */
const PREVIEW_ROWS = 5;
const DOWNLOAD_CAP = 1_000;
const OPS = [
  { label: "=", sql: "=" },   { label: "≠", sql: "!=" },
  { label: "<", sql: "<" },   { label: ">", sql: ">" },
  { label: "contains", sql: "contains" },
  { label: "startsWith", sql: "starts" },
] as const;
type OpKey = typeof OPS[number]["sql"];
interface Filter { id:number; column:string; op:OpKey; value:string }
interface Props   { conn:AsyncDuckDBConnection|null; tables:string[]; setMsg:(m:string)=>void }

/* ─────────────── localStorage helper ─────────────── */
function usePersist<T>(key:string, initial:T){
  const [state,set] = useState<T>(()=>{
    try{ const raw = localStorage.getItem(key); return raw?JSON.parse(raw):initial; }
    catch{ return initial; }
  });
  useEffect(()=>{ localStorage.setItem(key,JSON.stringify(state)); },[key,state]);
  return [state,set] as const;
}

/* ───────────────────────── FilterRow ───────────────────────── */
interface RowProps {
  row: Filter;
  cols: string[];
  conn: AsyncDuckDBConnection|null;
  dataTable: string;
  update: (id:number,patch:Partial<Filter>)=>void;
  remove: (id:number)=>void;
}
const FilterRow = memo(function FilterRow({
  row, cols, conn, dataTable, update, remove,
}: RowProps){
  /* column search --------------------------------------------------- */
  const [colDraft,setColDraft] = useState("");
  const [colFocused,setColFocused] = useState(false);
  const colMatches = useMemo(
    ()=>cols.filter(c=>c.toLowerCase().includes(colDraft.toLowerCase())).slice(0,40),
    [colDraft,cols],
  );
  const commitCol = (c:string)=>{
    if(cols.includes(c)) update(row.id,{ column:c });
    setColDraft("");
  };

  /* value input + suggestions --------------------------------------- */
  const [valDraft,setValDraft] = useState(row.value);
  const [valFocused,setValFocused] = useState(false);
  useEffect(()=>{ if(row.value!==valDraft) setValDraft(row.value); },[row.value]);
  const debVal = useDebounce(valDraft,800);
  useEffect(()=>{ if(debVal!==row.value) update(row.id,{ value:debVal }); },[debVal,row.id,update]);

  const [valSugs,setValSugs] = useState<string[]>([]);
  const debSug = useDebounce([row.column,valDraft] as const,300);
  useEffect(()=>{
    (async ()=>{
      const [col,term] = debSug;
      if(!conn||!dataTable||!col||!term.trim()) return setValSugs([]);
      try{
        const safe = term.replace(/'/g,"''");
        const sql  = `
          SELECT DISTINCT "${col}" AS v
          FROM "${dataTable}"
          WHERE CAST("${col}" AS TEXT) ILIKE '%${safe}%'
          LIMIT 20`;
        const out  = arrowTableToObjects(await conn.query(sql))
                       .map((r:any)=>r.v).filter(Boolean);
        setValSugs(out);
      }catch{ setValSugs([]); }
    })();
  },[debSug,conn,dataTable]);

  /* blur helpers: hide list **after** click captured ---------------- */
  const hideLater = (cb:()=>void)=>()=>setTimeout(cb,100);

  /* ─────────────── render ─────────────── */
  return (
    <div className="flex items-end gap-2 bg-background p-2 rounded">
      {/* column picker */}
      <div className="w-48 relative">
        <label className="text-xs">Column</label>
        <Input
          value={row.column||colDraft}
          placeholder="Column"
          onFocus={()=>setColFocused(true)}
          onBlur={hideLater(()=>setColFocused(false))}
          onChange={e=>setColDraft(e.target.value)}
          onKeyDown={e=>{ if(e.key==="Enter") commitCol(colDraft); }}
        />
        {colFocused && colDraft && colMatches.length>0 && (
          <ScrollArea className="absolute z-20 mt-1 w-full max-h-40 border rounded-md bg-background">
            {colMatches.map(c=>(
              <button
                key={c}
                className="block w-full text-left px-2 py-1 hover:bg-muted text-sm"
                onMouseDown={()=>commitCol(c)}
              >{c}</button>
            ))}
          </ScrollArea>
        )}
      </div>

      {/* operator */}
      <div className="w-24">
        <label className="text-xs">Op</label>
        <select
          className="w-full h-9 border rounded-md px-2 bg-background"
          value={row.op}
          onChange={e=>update(row.id,{ op:e.target.value as OpKey })}
        >
          {OPS.map(o=><option key={o.sql} value={o.sql}>{o.label}</option>)}
        </select>
      </div>

      {/* value input */}
      <div className="flex-1 relative">
        <label className="text-xs">Value</label>
        <Input
          value={valDraft}
          placeholder="Type value…"
          onFocus={()=>setValFocused(true)}
          onBlur={hideLater(()=>setValFocused(false))}
          onChange={e=>setValDraft(e.target.value)}
          onKeyDown={e=>{ if(e.key==="Enter"){ (e.target as HTMLInputElement).blur(); } }}
        />
        {valFocused && valSugs.length>0 && (
          <ScrollArea className="absolute z-20 mt-1 w-full max-h-40 border rounded-md bg-background">
            {valSugs.map(v=>(
              <button
                key={String(v)}
                className="block w-full text-left px-2 py-1 hover:bg-muted text-sm"
                onMouseDown={()=>{ setValDraft(String(v)); update(row.id,{ value:String(v) }); }}
              >{String(v)}</button>
            ))}
          </ScrollArea>
        )}
      </div>

      {/* remove */}
      <Button variant="ghost" size="icon" onClick={()=>remove(row.id)}>
        <CloseIcon className="h-4 w-4"/>
      </Button>
    </div>
  );
});

/* ───────────────────── main component ───────────────────── */
export default function FilterDownload({ conn, tables, setMsg }:Props){
  /* table selection */
  const [table,setTable] = useState("");

  /* filters (persisted per-table) */
  const nextId = useRef(1);
  const [filters ,setFilters ] = usePersist<Filter[]>(`fd-filters:${table}`,[]);
  const [applied ,setApplied ] = usePersist<Filter[]>(`fd-applied:${table}`,[]);
  const dirty = JSON.stringify(filters)!==JSON.stringify(applied);

  /* columns cache (temp table) + drop old temp on switch */
  const [columns,setColumns] = useState<string[]>([]);
  const lastTemp = useRef<string|null>(null);

  useEffect(()=>{
    (async ()=>{
      if(!conn){
        setColumns([]);
        return;
      }

      // Drop previous temp table if it differs from the new one
      if(lastTemp.current && lastTemp.current !== `_fd_cols_${table}`){
        try{ await conn.query(`DROP TABLE IF EXISTS "${lastTemp.current}"`); }
        // eslint-disable-next-line no-empty
        catch{}
        lastTemp.current = null;
      }

      if(!table){
        setColumns([]);
        return;
      }

      // Create/replace temp table for this selection
      const tmp = `_fd_cols_${table}`;
      await conn.query(
        `CREATE OR REPLACE TEMP TABLE "${tmp}" AS
         SELECT name FROM pragma_table_info('${table}')`
      );
      const res = await conn.query(`SELECT name FROM "${tmp}" ORDER BY name`);
      const names = arrowTableToObjects(res).map((r:any)=>r.name);
      setColumns(names);
      lastTemp.current = tmp;
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[conn,table]);

  // Clean up any leftover temp table when component unmounts
  useEffect(()=>()=>{
    if(conn && lastTemp.current){
      conn.query(`DROP TABLE IF EXISTS "${lastTemp.current}"`).catch(()=>{});
    }
  },[conn]);

  // Prune filters against new column list
  useEffect(()=>{
    setFilters(f=>f.filter(r=>!r.column || columns.includes(r.column)));
    setApplied(a=>a.filter(r=>!r.column || columns.includes(r.column)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[columns.length]);

  /* helpers */
  const addFilter = ()=>setFilters(f=>[...f,{id:nextId.current++,column:"",op:"=",value:""}]);
  const updateFilter = useCallback((id:number,p:Partial<Filter>)=>{
    setFilters(f=>f.map(r=>r.id===id?{...r,...p}:r));
  },[]);
  const removeFilter = useCallback((id:number)=>{
    setFilters(f=>f.filter(r=>r.id!==id));
  },[]);
  const clearFilters = ()=>{ setFilters([]); setApplied([]); };

  /* WHERE clause + preview query ------------------------------------ */
  const where = useMemo(()=>{
    if(!applied.length) return "";
    const clauses = applied.filter(f=>f.column&&f.value.trim()).map(f=>{
      const v = f.value.replace(/'/g,"''");
      switch(f.op){
        case "contains": return `CAST("${f.column}" AS TEXT) ILIKE '%${v}%'`;
        case "starts"  : return `CAST("${f.column}" AS TEXT) ILIKE '${v}%'`;
        default        : {
          const lit = /^\d+(\.\d+)?$/.test(v)||v.toLowerCase()==="null"?v:`'${v}'`;
          return `"${f.column}" ${f.op} ${lit}`;
        }
      }
    });
    return clauses.length?`WHERE ${clauses.join(" AND ")}`:"";
  },[applied]);

  const { data, cols, totalCount, error, run } = useQueryRunner(conn,null,PREVIEW_ROWS);
  const debWhere = useDebounce(where,250);
  useEffect(()=>{
    if(table) run(`SELECT * FROM "${table}" ${debWhere}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[table,debWhere]);

  /* CSV download */
  const [csvName,setCsvName] = useState("");
  const download = async ()=>{
    if(!conn||!table) return;
    const res  = await conn.query(`SELECT * FROM "${table}" ${where} LIMIT ${DOWNLOAD_CAP}`);
    const rows = arrowTableToObjects(res);
    if(!rows.length){ setMsg("No rows to download"); return; }
    exportToCsv(rows, cols.length?cols:Object.keys(rows[0]), `${csvName.trim()||table}.csv`);
    setMsg("CSV download started");
  };

  /* ────────────────────────── render ────────────────────────── */
  return (
    <Card className="h-full flex flex-col">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FilterIcon className="h-5 w-5 text-brand"/> Filter & Download
        </CardTitle>
        <CardDescription>
          Preview {PREVIEW_ROWS} rows · export up to {DOWNLOAD_CAP.toLocaleString()} rows
        </CardDescription>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col gap-4">
        {/* top bar */}
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1.5 min-w-[200px] flex-1">
            <label className="text-sm font-medium">Table</label>
            <select
              className="w-full h-9 border rounded-md px-2 bg-background"
              value={table}
              disabled={!tables.length}
              onChange={e=>setTable(e.target.value)}
            >
              <option value="" disabled>Select a table…</option>
              {tables.map(t=><option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          {!!table && (
            <Button variant="outline" size="icon" onClick={addFilter}>
              <Plus className="h-4 w-4"/>
            </Button>
          )}
        </div>

        {/* filter panel */}
        <div className="border rounded-md p-2 space-y-2 bg-muted/20 max-h-[320px] overflow-auto">
          {filters.length===0 ? (
            <p className="text-sm text-muted-foreground">
              No filters — click “＋” to add one.
            </p>
          ) : (
            filters.map(r=>(
              <FilterRow
                key={r.id}
                row={r}
                cols={columns}
                conn={conn}
                dataTable={table}
                update={updateFilter}
                remove={removeFilter}
              />
            ))
          )}

          <div className="flex gap-2 pt-1">
            <Button size="sm" disabled={!dirty} onClick={()=>setApplied(filters)} className="gap-1.5">
              <Check className="h-4 w-4"/>Apply
            </Button>
            {filters.length>0 && (
              <Button size="sm" variant="ghost" onClick={clearFilters}>Clear</Button>
            )}
          </div>
        </div>

        {/* preview + download */}
        <div className="relative flex-1 flex flex-col overflow-hidden">
          {error ? (
            <div className="flex-1 flex items-center justify-center text-destructive">
              {error.message}
            </div>
          ) : data.length ? (
            <>
              <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
                <span className="text-sm font-medium">
                  Showing first {PREVIEW_ROWS} / {totalCount.toLocaleString()} rows
                </span>
                <div className="flex items-center gap-2">
                  <Input value={csvName} onChange={e=>setCsvName(e.target.value)}
                         placeholder="file name" className="h-8 w-40"/>
                  <Button variant="outline" size="sm" onClick={download}>
                    <DownloadIcon className="h-4 w-4 mr-1"/>CSV
                  </Button>
                </div>
              </div>
              <DataTable data={data} columns={cols} maxHeight="300px" colorScheme="gray"/>
            </>
          ) : table ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              No rows match these filters
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              Select a table to get started
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
